import { 
  Component, EventEmitter, Input, OnDestroy, OnInit, 
  Output, TemplateRef, Type, ViewEncapsulation 
} from '@angular/core';
import { map, Observable, Subscription } from 'rxjs';
import { MatPaginatorModule } from '@angular/material/paginator';
import { CommonModule, NgClass, NgTemplateOutlet } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatTableModule } from '@angular/material/table';
import { MatSortModule } from '@angular/material/sort';
import { MatGridListModule } from '@angular/material/grid-list';
import { TableColumnInfo, TypeOfColumn } from './list-page.utils';
import { MatDividerModule } from '@angular/material/divider';
import { BreakpointObserver } from '@angular/cdk/layout';
import { MatIcon } from '@angular/material/icon';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { Q8Breakpoints } from 'core';
import { TranslocoCurrencyPipe, TranslocoDecimalPipe } from '@jsverse/transloco-locale';
import { MatCardModule } from '@angular/material/card';

@Component({
  selector: 'ui-list-page',
  imports: [
    MatTableModule, CommonModule,
    ReactiveFormsModule, FormsModule, MatButtonModule,
    MatPaginatorModule, MatSortModule, NgClass,
    NgTemplateOutlet, MatGridListModule, MatDividerModule,
    MatIcon, MatButtonToggleModule, TranslocoCurrencyPipe, TranslocoDecimalPipe,
    MatCardModule
  ],
  templateUrl: './list-page.html',
  styleUrl: './list-page.scss',
  encapsulation: ViewEncapsulation.None,
})
export class ListPage implements OnInit, OnDestroy {
  // Makes the TypeOfColumn enum available in the template
  TypeOfColumn = TypeOfColumn;

  @Input() columnsInfo: TableColumnInfo[];

  // Template da usare per disegnare l'item quando si è in modalità grid
  @Input() gridItemTemplate: TemplateRef<any>;

  // The API call to invoke to obtain the data
  @Input() dataSource: (pageSize?: number, pageNumber?: number, payload?: any) => Observable<any>;

  // Transform and adapt call result data for the list
  @Input() dataTransform: (dataResult?: any) => {listData: any, totalItems: number};

  @Input() pageSizeOptions: number[] = [1, 5, 10, 25, 100];

  @Input() currentSortColumn: string; // colonna per cui ordinare inizialmente
  @Input() currentSortDirection: 'asc' | 'desc' | '' = '';

  @Input() initialPage: number = 1;
  @Input() initialPageSize: number = 10;

  @Input() showPaginator: boolean;

  @Input() showToggleView: boolean;

  @Input() showListCard: boolean = false;

  // Click row event
  @Output() clickedRowAction = new EventEmitter<any>();


  // Reload data event
  reloadDataEmitter = new EventEmitter();
  reloadDataSubscription: Subscription;

  // Pagination
  currentPage: number = 1;
  currentPageSize: number = 10;

  currentSortColumns: { column: string; direction: string }[] = [];

  dataSourceTable: any[] = [];
  totalItems: number = 0;

  // Default 1 col == mobile first
  gridColumns: number = 1;

  get displayedColumns() {
    return this.columnsInfo.map(column => column.columnNameDef);
  }

  constructor(
    private breakpointObserver: BreakpointObserver
  ) {

  }

  ngOnInit(): void {
    // Subscribe for management reload data from childs
    this.reloadDataSubscription = this.reloadDataEmitter.subscribe(() => {
      this.loadData();
    });

    // Inizializzo la paginazione con i valori passati dall'esterno (per sincronizzare tra viste diverse)
    this.currentPage = this.initialPage;
    this.currentPageSize = this.initialPageSize;

    //TODO: aggioranre queryparams sulla base di eventuali filtri applicati

    // Recupero dati della pagina con chiamata API
    this.loadData();

    // Osservo breakpoint variazione
    this.breakpointObserver.observe([
      `(min-width: ${Q8Breakpoints.tabletLandscape}px)`
    ]).subscribe(result => {
      // risult.matches è true se sopra a '(min-width: 601px)'
      if (result.matches) {
        this.gridColumns = 2; // altri schermi
      } else {
        this.gridColumns = 1; // mobile
      }
    });
  }

  ngOnDestroy() {
    this.reloadDataSubscription?.unsubscribe();
  }

  changeListType(isToShowListCard: boolean) {
    this.showListCard = isToShowListCard;
  }

  // Metodo contenente la gestione della chiamata per la lista di elementi
  private loadData() {
    let payload = this.createPayload();

    // Invoke api call read from [dataSource].
    let apiCall = this.dataSource(this.currentPageSize, this.currentPage, payload);
    
    apiCall.pipe(
      map(dataResult => 
        {
          // console.log("api call - pipe", dataResult);

          // Invoke the transformation method of the result read from [dataTrasform]
          let dataTrasformed = this.dataTransform(dataResult);
      
          this.dataSourceTable = dataTrasformed.listData;
          this.totalItems = dataTrasformed.totalItems;

          // Controllo che la pagina corrente non sia maggiore del numero massimo di pagine se si resetto a 1
          const maxPage = Math.ceil(this.totalItems / this.currentPageSize);
          if(this.totalItems !== 0 && this.currentPage !== 1 && this.currentPage > maxPage) {
            console.warn('currentPage > maxPage, resetting to 1');
            this.currentPage = 1
            this.loadData();
          }
        })
    ).subscribe();
  }

  // Create the payload for the api call
  private createPayload() {
    let payload: any = {};
    // leggo form di ricerca testuale
    // leggo form di ricerca avanzata/filtri

    // aggiungo/modifico il payload con i dati dei form sopra
    if(this.currentSortColumns.length > 0){
      payload["OrderBy"] = this.currentSortColumns.map((sort) => ({
        PropertyName: sort.column,
        OrderType: sort.direction,
      }));
    }

    return payload;
  }

  handlePageEvent(event: any) {
    // console.log("handlePageEvent", event);

    this.currentPage = event.pageIndex + 1;
    this.currentPageSize = event.pageSize;

    this.loadData();

    // aggiornare query params in url
  }

  handleSortEvent(sortEvent: any) {
    console.log("handleSortEvent", sortEvent);
    //TODO: verificare casi d'uso

    // aggiornare query params in url

    // this.loadData();
  }

  handleMultiSortEvent(columnName: string): void {
    const existingSortIndex = this.currentSortColumns.findIndex(sort => sort.column === columnName);
  
    if (existingSortIndex !== -1) {
      // Cambio la direzione se la colonna è già ordinata o rimuove l'ordinamento
      const currentDirection = this.currentSortColumns[existingSortIndex].direction;
        if(currentDirection === 'asc') {
          this.currentSortColumns = [{ column: columnName, direction: 'desc' }];
        } else{
          this.currentSortColumns = [];
        }
    } else {
      // Aggiungo la colonna con ordinamento asc
      this.currentSortColumns = [{ column: columnName, direction: 'asc' }];
    }

    // aggiornare query params in url

    this.loadData();
  }

  isColumnSorted(columnName: string): boolean {
    return this.currentSortColumns.some(sort => sort.column === columnName);
  }

  getSortDirection(columnName: string): string | null {
    const sort = this.currentSortColumns.find(sort => sort.column === columnName);
    return sort ? sort.direction : null;
  }

  handleRowClicked(row: any) {
    // console.log("handleRowClicked", row);
    this.clickedRowAction.emit(row);
  }

  // Metodo utilizzabile per ricaricare i dati
  public reloadData() {
    this.loadData();
  }
}
